import React from "react";
import { ThemeConfig, getThemeValue } from "../hooks/useVoiceWidget";
import { getStrings, Language } from "../i18n/strings";
import { HeyAiMicrophoneIcon, HeyAiTextIcon } from "./HeyAiIcons";
import { useMediaQuery } from "../hooks/useMediaQuery";

interface ChatCollapsedButtonProps {
  buttonState: "idle" | "connecting" | "recording" | "disabled";
  theme?: Partial<ThemeConfig>;
  onExpand: () => void;
  onVoiceStart?: () => void;
  avatarUrl?: string;
  ariaLabel?: string;
  language?: Language;
  mode?: "chat" | "voice" | "combined";
  uiVariant?: "legacy" | "split-switch";
  widgetName?: string | null;
}

const ChatCollapsedButton: React.FC<ChatCollapsedButtonProps> = ({
  buttonState,
  theme,
  onExpand,
  onVoiceStart,
  avatarUrl,
  ariaLabel,
  language,
  mode = "chat",
  uiVariant = "legacy",
  widgetName,
}) => {
  const t = getStrings(language);
  const sizePx = parseInt(getThemeValue(theme, "buttonSize"), 10) || 56;
  const buttonSize = `${sizePx}px`;
  const useSplitSwitch = mode === "combined" && uiVariant === "split-switch";
  const [pickerOpen, setPickerOpen] = React.useState(false);
  // Inline px below can't be reached by a CSS @media rule, so shrink on a
  // narrow viewport here to stop the picker clipping past the screen edge.
  const isMobile = useMediaQuery("(max-width: 480px)");

  if (useSplitSwitch) {
    const isRecording = buttonState === "recording";
    const avatarPx = Math.max(sizePx, 60);
    const initial = (widgetName || t.fallbackName || "A").trim().charAt(0).toUpperCase();
    const pickerControlBaseStyle: React.CSSProperties = {
      appearance: "none",
      border: 0,
      background: "transparent",
      color: "inherit",
      width: isMobile ? "62px" : "76px",
      height: isMobile ? "54px" : "58px",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      gap: "5px",
      fontSize: isMobile ? "12px" : "13px",
      fontWeight: 600,
      lineHeight: 1.05,
      fontFamily: "inherit",
      letterSpacing: "0",
      whiteSpace: "nowrap",
      cursor: "pointer",
    };

    return (
      <div
        style={{
          display: "inline-flex",
          flexDirection: "row-reverse",
          alignItems: "center",
          justifyContent: "flex-start",
          gap: "10px",
          fontFamily: getThemeValue(theme, "fontFamily"),
        }}
      >
        {/* Avatar launcher: visible in State 1; tap reveals the picker. */}
        <button
          data-testid="launcher-avatar"
          data-voice-active={isRecording ? "true" : undefined}
          className="heyai-launcher-button"
          type="button"
          onClick={() => {
            if (isRecording) {
              onVoiceStart?.();
            } else {
              setPickerOpen((open) => !open);
            }
          }}
          aria-label={ariaLabel || t.openAssistant}
          aria-expanded={pickerOpen}
          style={{
            position: "relative",
            width: avatarPx,
            height: avatarPx,
            borderRadius: "50%",
            overflow: "visible",
            padding: 0,
            cursor: "pointer",
            background: "rgba(255, 255, 255, 0.22)",
            backdropFilter: "blur(22px) saturate(140%)",
            WebkitBackdropFilter: "blur(22px) saturate(140%)",
            border: "1px solid rgba(255, 255, 255, 0.88)",
            boxShadow:
              "0 14px 35px rgba(6, 10, 18, 0.24), inset 0 1px 0 rgba(255,255,255,0.6)",
          }}
        >
          {avatarUrl ? (
            <img
              src={avatarUrl}
              alt={widgetName || t.fallbackName}
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                borderRadius: "50%",
                display: "block",
              }}
            />
          ) : (
            <span
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "100%",
                height: "100%",
                borderRadius: "50%",
                color: "rgba(17,24,39,0.78)",
                fontWeight: 600,
                fontSize: `${Math.round(avatarPx * 0.38)}px`,
              }}
            >
              {initial}
            </span>
          )}

          {/* Voice-active pulse: glow plus concentric rings for State 3. */}
          {isRecording && (
            <>
              <div
                style={{
                  position: "absolute",
                  inset: "-14px",
                  borderRadius: "50%",
                  background:
                    "var(--heyai-pulse-gradient, linear-gradient(135deg, rgba(139,92,246,0.38), rgba(119,175,255,0.52)))",
                  animation: "heyaiGlow 2.4s ease-in-out infinite",
                  pointerEvents: "none",
                  zIndex: -1,
                }}
              />
              <div
                data-testid="voice-pulse"
                style={{
                  position: "absolute",
                  inset: "-8px",
                  borderRadius: "50%",
                  border: "1.5px solid rgba(119,175,255,0.65)",
                  animation: "heyaiPulse 1.8s ease-out infinite",
                  pointerEvents: "none",
                }}
              />
              <div
                style={{
                  position: "absolute",
                  inset: "-8px",
                  borderRadius: "50%",
                  border: "1px solid rgba(139,92,246,0.42)",
                  animation: "heyaiPulse2 1.8s ease-out infinite 0.6s",
                  pointerEvents: "none",
                }}
              />
            </>
          )}
        </button>

        {/* Compact Voice/Text picker for State 2. */}
        {pickerOpen && !isRecording && (
          <div
            data-testid="mode-picker"
            className="heyai-mode-picker-motion"
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "0",
              borderRadius: "23px",
              overflow: "hidden",
              padding: "6px",
              color: "rgba(255, 255, 255, 0.95)",
              backdropFilter: "blur(22px) saturate(140%)",
              WebkitBackdropFilter: "blur(22px) saturate(140%)",
              background:
                "linear-gradient(145deg, rgba(28,30,38,0.54), rgba(28,30,38,0.38))",
              border: "1px solid rgba(255, 255, 255, 0.24)",
              boxShadow: "0 18px 42px rgba(10, 18, 32, 0.24), inset 0 1px 0 rgba(255,255,255,0.16)",
              fontFamily: getThemeValue(theme, "fontFamily"),
            }}
            aria-label={ariaLabel || t.openAssistant}
          >
            <button
              data-testid="mode-picker-text"
              className="heyai-mode-picker-control"
              type="button"
              onClick={() => {
                setPickerOpen(false);
                onExpand();
              }}
              style={{
                ...pickerControlBaseStyle,
                borderRadius: "17px 7px 7px 17px",
              }}
            >
              <HeyAiTextIcon size={18} />
              {t.textMode}
            </button>

            <div
              style={{
                width: "1px",
                height: "44px",
                background: "linear-gradient(to bottom, transparent, rgba(255,255,255,0.20), transparent)",
                flexShrink: 0,
              }}
            />

            <button
              data-testid="mode-picker-voice"
              className="heyai-mode-picker-control"
              type="button"
              onClick={() => {
                setPickerOpen(false);
                onVoiceStart?.();
              }}
              disabled={!onVoiceStart || buttonState === "disabled" || buttonState === "connecting"}
              style={{
                ...pickerControlBaseStyle,
                borderRadius: "7px 17px 17px 7px",
                cursor:
                  !onVoiceStart || buttonState === "disabled" || buttonState === "connecting"
                    ? "not-allowed"
                    : "pointer",
                opacity:
                  !onVoiceStart || buttonState === "disabled" || buttonState === "connecting"
                    ? 0.5
                    : 1,
              }}
              aria-label={buttonState === "connecting" ? t.statusConnecting : t.startVoice}
            >
              <HeyAiMicrophoneIcon size={18} />
              {buttonState === "connecting" ? t.statusConnecting : t.voiceMode}
            </button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      style={{
        position: "relative",
        display: "inline-block",
      }}
    >
      {/* Chat Button */}
      <button
        onClick={onExpand}
        style={{
          width: buttonSize,
          height: buttonSize,
          borderRadius: "50%",
          border: "none",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          transition: "all 0.3s ease",
          background: avatarUrl ? "transparent" : getThemeValue(theme, "buttonBackground"),
          boxShadow: `0 4px 16px rgba(99, 102, 241, 0.4)`,
          fontFamily: getThemeValue(theme, "fontFamily"),
          outline: "none",
          position: "relative",
          color: getThemeValue(theme, "buttonColor"),
          fontSize: `${Math.round(sizePx * 0.43)}px`,
          padding: 0,
          overflow: "hidden",
        }}
        aria-label={ariaLabel || t.openChat}
      >
        {avatarUrl ? (
          <img
            src={avatarUrl}
            alt=""
            style={{ width: "100%", height: "100%", objectFit: "cover" }}
          />
        ) : (
          "💬"
        )}
      </button>

      {/* Compact status display when active */}
      {buttonState === "connecting" && (
        <div
          style={{
            background: getThemeValue(theme, "statusBackground"),
            backdropFilter: "blur(8px)",
            borderRadius: "8px",
            padding: "4px 8px",
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
            border: "1px solid #e2e8f0",
            fontSize: "10px",
            color: getThemeValue(theme, "statusColor"),
            fontWeight: "500",
            textAlign: "center",
            whiteSpace: "nowrap",
            position: "absolute",
            top: "100%",
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 1000,
            fontFamily: getThemeValue(theme, "fontFamily"),
          }}
        >
          {t.statusConnecting}
        </div>
      )}
    </div>
  );
};

export default ChatCollapsedButton;
